import { FC, useCallback, useMemo, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

import { BackButton } from 'components/UI/BackButton';
import { Button } from 'components/UI/Button';
import { Heading } from 'components/UI/Heading';
import { InputField } from 'components/UI/InputField';
import { Loader } from 'components/UI/Loader';
import { SuccessState } from 'components/UI/SuccessState';
import { useTranslations } from 'hooks/useTranslations';
import { useQuery } from 'hooks/useQuery';
import { useBrand } from 'hooks/useBrand';
import { useAppSelector } from 'hooks/redux';
import { RecoverPasswordData } from 'models/diam';
import { localizationSelector } from 'store/localizationState';
import { useRecoverPasswordMutation } from 'store/diamApi';
import { mailRegex } from 'utils/constants';
import { Description, InputFieldContainer } from './styled';

export const ResetPasswordPage: FC = () => {
  const t = useTranslations();
  const { brand } = useBrand();
  const { languageCode } = useAppSelector(localizationSelector);
  const [redirectUrl] = useQuery('redirectUrl');

  // State
  const [emailAddress, setEmailAddress] = useState<string | null>(null);

  // Redux
  const [recoverPassword, recoverPasswordResult] = useRecoverPasswordMutation();

  // Zod schema
  const schema = useMemo(
    () =>
      z.object({
        username: z
          .string()
          .min(1, t('validation.required'))
          .regex(mailRegex, t('validation.email_address'))
      }),
    [t]
  );

  type FormData = z.infer<typeof schema>;

  // useForm
  const {
    register,
    handleSubmit,
    formState: { errors }
  } = useForm<FormData>({
    resolver: zodResolver(schema)
  });

  // Submit
  const onSubmit: SubmitHandler<FormData> = useCallback(
    ({ username }) => {
      const passwordRecoveryData: RecoverPasswordData = {
        email: username,
        meta: {
          language: languageCode,
          ...(brand && { brand }),
          ...(redirectUrl && {
            forward: { uri: redirectUrl, text: t('general.go_to_login') }
          })
        }
      };

      recoverPassword(passwordRecoveryData);
      setEmailAddress(username);
    },
    [brand, languageCode, redirectUrl, recoverPassword, t]
  );

  if (recoverPasswordResult.isLoading) {
    return <Loader />;
  }

  if (recoverPasswordResult.isSuccess) {
    return (
      <SuccessState
        heading={t('reset_password.title_success')}
        details={
          <>
            <Description $center>
              {t('reset_password.description_reset_password_email')}
            </Description>
            <Description $center>
              <strong>{emailAddress}</strong>
            </Description>
          </>
        }
        iconType="email"
      />
    );
  }

  return (
    <>
      <BackButton />
      <Heading>{t('reset_password.title')}</Heading>
      <Description>{t('reset_password.description')}</Description>
      <form onSubmit={handleSubmit(onSubmit)}>
        <InputFieldContainer>
          <InputField
            id="username"
            label={t('reset_password.label_email_address')}
            required
            placeholder={t('reset_password.placeholder_email_address')}
            error={errors.username}
            register={register('username')}
          />
        </InputFieldContainer>
        <Button type="submit">
          {t('reset_password.send_password_reset_link')}
        </Button>
      </form>
    </>
  );
};
